/**
 * GENERATED by scripts/generate-nav-tree.mjs from
 * docs/INVENTORY/manager-nav.json — do not edit by hand.
 *
 * The sidebar exactly as legacy composes it: 10 sections,
 * 51 items,
 * each carrying the permission and tier gate it is shown under.
 */
import {
  BarChart3,
  Book,
  Box,
  Boxes,
  Building2,
  Calculator,
  CalendarClock,
  CalendarDays,
  Car,
  ChefHat,
  ClipboardList,
  Clock,
  CookingPot,
  Cpu,
  CreditCard,
  Database,
  FileText,
  Gift,
  GripVertical,
  Heart,
  LayoutDashboard,
  LayoutTemplate,
  MapPin,
  Package,
  Palette,
  PlusCircle,
  Receipt,
  Scale,
  ScrollText,
  Settings2,
  Shield,
  Slice,
  Store,
  Tags,
  TrendingUp,
  Truck,
  Users,
  Utensils,
  Wine,
  type LucideIcon,
} from 'lucide-react'

export interface NavGuard {
  permission?: string
  /**
   * Either of several permissions - §9.302. A hub entry reaches more than one screen, so gating it
   * on one of their permissions would hide it from somebody who holds the other. Matches the
   * route's own guard, which module-contract.ts has always allowed.
   */
  anyPermission?: readonly string[]
  /**
   * The roles admitted, when the **route** restricts by role and not only by permission - §9.345.
   * Five entries carry one because every endpoint behind them admits main_admin and admin only, and
   * a sidebar that offers what the router refuses is §9.7's defect. access.ts has always evaluated
   * this key; the generator could neither emit it nor type it.
   *
   * No backticks in this comment on purpose: it is emitted from inside a template literal.
   */
  systemRoles?: readonly string[]
  /**
   * Also admits somebody whose role manages another role - O119, FRONTEND.md section 9.753. It lifts the role and
   * permission gates and keeps the tier gate, as the route's own RouteGuard key of the same name does.
   *
   * No backticks in this comment either: it is emitted from inside a template literal.
   */
  orManagesRoles?: boolean
  franchiseModule?: string
  superAdminOnly?: boolean
}

export interface NavItem {
  /** The Slovak source string; wrapped in <T> when rendered. */
  label: string
  path: string
  icon: LucideIcon | null
  guard: NavGuard | undefined
}

export interface NavSection {
  heading: string
  sectionKey: string | null
  collapsible: boolean
  guard: NavGuard | undefined
  items: NavItem[]
}

export const NAV_SECTIONS: NavSection[] = [
  {
    heading: "Správa Reštaurácie",
    sectionKey: null,
    collapsible: false,
    guard: undefined,
    items: [
      {
        label: "Prehľad",
        path: "/dashboard",
        icon: LayoutDashboard,
        guard: { permission: "dashboard" },
      },
      {
        label: "Nová objednávka",
        path: "/new-order",
        icon: PlusCircle,
        guard: { permission: "new_order" },
      },
      {
        label: "Predobjednávky",
        path: "/preorders",
        icon: CalendarClock,
        guard: { permission: "preorders" },
      },
      {
        label: "Kuchyňa",
        path: "/kitchen",
        icon: ChefHat,
        guard: { permission: "kitchen" },
      },
      {
        label: "Rozvoz - nástenka",
        path: "/dispatch",
        icon: MapPin,
        guard: { permission: "dispatch" },
      },
      {
        label: "Otvorené stoly",
        path: "/restaurant",
        icon: Utensils,
        guard: { permission: "restaurant" },
      },
      {
        label: "Mapovanie stolov",
        path: "/table-map",
        icon: GripVertical,
        guard: { permission: "restaurant", franchiseModule: "restaurant.tables" },
      },
    ],
  },
  {
    heading: "Správa Kuchyne",
    sectionKey: null,
    collapsible: false,
    guard: undefined,
    items: [
      {
        label: "Kategórie",
        path: "/categories",
        icon: Tags,
        guard: { permission: "categories" },
      },
      {
        label: "Produkty",
        path: "/products",
        icon: Package,
        guard: { permission: "products" },
      },
      {
        label: "Obedové menu",
        path: "/lunch-menu",
        icon: Utensils,
        guard: { permission: "lunch_menu" },
      },
      {
        label: "Kombo BOX",
        path: "/kombo-box",
        icon: Box,
        guard: { permission: "burger_box" },
      },
      {
        label: "Pizza",
        path: "/pizza",
        icon: Slice,
        guard: { permission: "pizza" },
      },
    ],
  },
  {
    heading: "Skladové hospodárstvo",
    sectionKey: "stock-management",
    collapsible: true,
    guard: undefined,
    items: [
      {
        label: "Skladové hospodárstvo",
        path: "/stock",
        icon: Boxes,
        guard: { permission: "stock" },
      },
      {
        label: "Recepty",
        path: "/recipes",
        icon: CookingPot,
        guard: { permission: "recipes", franchiseModule: "stock.recipes" },
      },
      {
        label: "Nápojový sklad",
        path: "/beverage-stock",
        icon: Wine,
        guard: { permission: "beverage_stock", franchiseModule: "stock.beverage" },
      },
    ],
  },
  {
    heading: "Analytika",
    sectionKey: null,
    collapsible: false,
    guard: undefined,
    items: [
      {
        label: "Analytika",
        path: "/analytics",
        icon: TrendingUp,
        guard: { permission: "analytics", franchiseModule: "analytics" },
      },
      {
        label: "Sumáre",
        path: "/summaries",
        icon: FileText,
        guard: { permission: "summaries" },
      },
    ],
  },
  {
    heading: "Správa firmy",
    sectionKey: "company-management",
    collapsible: true,
    guard: { permission: "company_management" },
    items: [
      {
        label: "Prehľad firmy",
        path: "/reports",
        icon: Building2,
        guard: { permission: "company_overview", franchiseModule: "reports" },
      },
      {
        label: "Externé Firmy",
        path: "/suppliers",
        icon: Truck,
        guard: { anyPermission: ["suppliers","outgoing_invoices"] },
      },
      {
        label: "Porovnanie cien",
        path: "/supplier-comparison",
        icon: Scale,
        guard: { permission: "supplier_comparison", franchiseModule: "suppliers.comparison" },
      },
      {
        label: "Faktúry",
        path: "/invoices",
        icon: Receipt,
        guard: { permission: "invoices", franchiseModule: "invoices.incoming" },
      },
      {
        label: "Vydané faktúry",
        path: "/outgoing-invoices",
        icon: FileText,
        guard: { permission: "outgoing_invoices", franchiseModule: "invoices.outgoing" },
      },
      {
        label: "Návrhy objednávok",
        path: "/reorder-drafts",
        icon: Truck,
        guard: { permission: "reorder_drafts", franchiseModule: "reorder_drafts" },
      },
      {
        label: "Zoznamy & Archív",
        path: "/lists",
        icon: ClipboardList,
        guard: { permission: "lists" },
      },
      {
        label: "Fiškálne doklady",
        path: "/fiskal-receipts",
        icon: FileText,
        guard: { permission: "fiskal_receipts_view", franchiseModule: "fiskal" },
      },
      {
        label: "Pokladňa - uzávierky",
        path: "/fiskal-closures",
        icon: Receipt,
        guard: { permission: "fiskal_closures_view", franchiseModule: "fiskal" },
      },
      {
        label: "Platby online - archív",
        path: "/online-payments",
        icon: CreditCard,
        guard: { anyPermission: ["online_payments","online_payments_view"], franchiseModule: "online_payments" },
      },
    ],
  },
  {
    heading: "Správa rozvoz",
    sectionKey: "delivery-management",
    collapsible: true,
    guard: { permission: "delivery_management" },
    items: [
      {
        label: "Rozvoz",
        path: "/delivery-statistics",
        icon: BarChart3,
        guard: { permission: "delivery_statistics", franchiseModule: "delivery_statistics" },
      },
      {
        label: "Externý Šofér",
        path: "/external-driver",
        icon: Truck,
        guard: { permission: "external_driver", franchiseModule: "external_driver" },
      },
      {
        label: "Vozový Park",
        path: "/vehicle-fleet",
        icon: Car,
        guard: { permission: "vehicle_fleet", franchiseModule: "vehicle_fleet" },
      },
      {
        label: "Kniha jázd",
        path: "/trip-log",
        icon: Book,
        guard: { permission: "trip_log", franchiseModule: "trip_log" },
      },
      {
        label: "Nastavenia rozvozu",
        path: "/delivery-settings",
        icon: Settings2,
        guard: { permission: "delivery_settings" },
      },
      {
        label: "Oblasti doručenia",
        path: "/delivery-zones",
        icon: MapPin,
        guard: { permission: "delivery_zones" },
      },
    ],
  },
  {
    heading: "Marketing",
    sectionKey: "marketing",
    collapsible: true,
    guard: { permission: "marketing" },
    items: [
      {
        label: "Zákazníci",
        path: "/marketing-customers",
        icon: Users,
        guard: { permission: "loyalty_program" },
      },
      {
        label: "Vernostný program",
        path: "/loyalty-program",
        icon: Heart,
        guard: { permission: "loyalty_program" },
      },
      {
        label: "Darčeky k objednávke",
        path: "/gifts",
        icon: Gift,
        guard: { permission: "gifts" },
      },
      {
        label: "Kupóny a zľavy",
        path: "/coupons",
        icon: Gift,
        guard: { permission: "coupons" },
      },
    ],
  },
  {
    heading: "Zamestnanci",
    sectionKey: "employees",
    collapsible: true,
    guard: undefined,
    items: [
      {
        label: "Zamestnanci",
        path: "/employees",
        icon: Users,
        guard: { permission: "employees" },
      },
      {
        label: "Oprávnenia rolí",
        path: "/permissions",
        icon: Shield,
        guard: { permission: "permissions_admin", franchiseModule: "permissions", systemRoles: ["main_admin"], orManagesRoles: true },
      },
      {
        label: "Dochádzka",
        path: "/attendance",
        icon: CalendarDays,
        guard: { permission: "attendance" },
      },
      {
        label: "Odovzdanie tržby",
        path: "/revenue-handovers",
        icon: Calculator,
        guard: { permission: "revenue_handovers", franchiseModule: "revenue_handovers" },
      },
    ],
  },
  {
    heading: "Systémové nastavenia",
    sectionKey: "system-settings",
    collapsible: true,
    guard: { permission: "system_settings" },
    items: [
      {
        label: "Design stránky",
        path: "/design",
        icon: Palette,
        guard: { permission: "design" },
      },
      {
        label: "CMS Stránky",
        path: "/cms",
        icon: LayoutTemplate,
        guard: { permission: "cms" },
      },
      {
        label: "Otváracie hodiny",
        path: "/opening-hours",
        icon: Clock,
        guard: { permission: "opening_hours" },
      },
      {
        label: "Pokladňa - nastavenia",
        path: "/fiskal",
        icon: Receipt,
        guard: { permission: "fiskal_settings", franchiseModule: "fiskal" },
      },
      {
        label: "Logy",
        path: "/system-logs",
        icon: ScrollText,
        guard: { permission: "system_logs", franchiseModule: "system_logs", systemRoles: ["main_admin","admin"] },
      },
      {
        label: "Pobočky (Super Admin)",
        path: "/franchises",
        icon: Store,
        guard: { superAdminOnly: true },
      },
      {
        label: "Admini pobočiek (Super Admin)",
        path: "/franchise-admins",
        icon: Users,
        guard: { superAdminOnly: true },
      },
    ],
  },
  {
    heading: "Externé služby",
    sectionKey: "extern-slu-by",
    collapsible: true,
    guard: { permission: "system_settings" },
    items: [
      {
        label: "Rozvozové platformy",
        path: "/settings-integrations",
        icon: Truck,
        guard: { permission: "settings_integrations", franchiseModule: "settings_integrations", systemRoles: ["main_admin","admin"] },
      },
      {
        label: "Zálohy databázy",
        path: "/settings-backups",
        icon: Database,
        guard: { permission: "settings_backups", franchiseModule: "backups", systemRoles: ["main_admin","admin"] },
      },
      {
        label: "Hikvision zariadenia",
        path: "/hikvision",
        icon: Cpu,
        guard: { permission: "hikvision", franchiseModule: "hikvision", systemRoles: ["main_admin","admin"] },
      },
    ],
  },
]
